import { useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconRedo } from '@components/icons/redo';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { Box } from 'components/Box';
import { FileUpload } from 'components/FileUpload';
import { InlineMultiSelect } from 'components/InlineMultiSelect';
import { IntegrationField } from 'components/IntegrationField';
import { IntegrationHeader } from 'components/IntegrationHeader';
import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { useModel } from 'hooks/useModel';
import { IntegrationData } from 'models/integrations';
import { useStore } from 'models/Provider';

import { SignetTrigger } from './Models';
import { QuickUpload } from './QuickUpload';

export const SignetOnboarding = observer(({ base, validator }) => {
  const { api, applicationStore, t } = useStore();
  const { showSuccess, showError } = useToaster();
  const { showModal, showFullscreenModal } = useModal();
  const integration = useModel(
    SignetTrigger,
    {
      triggerId: base.triggerId,
      name: base.name,
      title: base.title,
      description: base.description,
      successMessage: base.successMessage,
      applicationId: base.parent.id,
    },
    { api, applicationStore, showSuccess, showError, t }
  );

  async function submit(retrigger, save, id) {
    const contract = integration.multipleData.get(id);
    if (save || contract.isValid()) {
      await contract.submit(retrigger, save);
    } else {
      const firstError = contract.firstError();
      if (firstError && firstError.name) {
        const el = document.querySelector(`[data-id=${firstError.name}]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }

  async function preview(id) {
    const contract = integration.multipleData.get(id);
    const previewUrl = await contract.preview();
    const data = IntegrationData.create({ value: previewUrl }, { api });
    showFullscreenModal('INTEGRATION_FILE_PREVIEW', { data });
  }

  function removeModal(id) {
    showModal('CONFIRM_MODAL', {
      _container: {
        autoWidth: true,
      },
      negative: true,
      data: { id: id },
      headerText: t('modals.removeconfirm.contract.header'),
      mainText: t('modals.removeconfirm.contract.main'),
      confirmText: t('modals.removeconfirm.contract.button'),
      confirm: remove,
    });
  }

  async function remove(id) {
    const contract = integration.multipleData.get(id);
    if (contract.saved) {
      await contract.delete();
    }
    integration.removeMultiDataItem(contract);
    return true;
  }

  const [templateFiles, setTemplateFiles] = useState({});

  async function setFile(id, file) {
    const contract = integration.multipleData.get(id);
    const url = await readURL(file);
    contract.setFile(file.name, url);
    setTemplateFiles((old) => {
      return { ...old, [id]: file };
    });
    contract.appendQuickUpload();
  }

  return (
    <>
      <IntegrationHeader integration={integration} />
      {integration.state === 'loading' && <Loading />}
      {Array.from(integration.multipleData.entries()).map(
        ([
          ix,
          {
            id,
            inProgress,
            fileUrl,
            actions,
            items,
            loading,
            template,
            templateChoices,
            needsCosigners,
            cosigners,
            cosignersChoices,
            refetch,
            hasErrors,
          },
        ]) => {
          if (inProgress) {
            return (
              <Box key={id} border padding="24" mt="16" backgroundColor="white">
                <Stack space={4}>
                  {items.map((data, i) => (
                    <IntegrationField data={data} validator={validator} key={i} readOnly />
                  ))}
                </Stack>
                <Box flex justifyContent="flex-end" mt="20">
                  {actions.map((action, i) => {
                    switch (action) {
                      case 'delete':
                        return (
                          <Box ml="8" key={i}>
                            <IconButton
                              tooltip={t('application.onboarding.integration.delete')}
                              icon={<IconTrash />}
                              onClick={() => removeModal(id)}
                            />
                          </Box>
                        );
                      case 'preview':
                        return (
                          <Box ml="8" key={i}>
                            <IconButton
                              tooltip={t('application.onboarding.integration.preview')}
                              icon={<IconVisibilityShown />}
                              onClick={() =>
                                showFullscreenModal('CUSTOM_FILE_PREVIEW', { filePath: fileUrl })
                              }
                            />
                          </Box>
                        );
                      case 'resend':
                        return (
                          <Box ml="8" key={i}>
                            <IconButton
                              tooltip={t('application.onboarding.integration.preview')}
                              icon={<IconRedo />}
                              onClick={() => preview(id)}
                            />
                          </Box>
                        );
                      default:
                        return null;
                    }
                  })}
                </Box>
              </Box>
            );
          }
          if (templateFiles[id]) {
            return (
              <QuickUpload
                id={id}
                loading={loading}
                cosigners={cosigners}
                remove={remove}
                cosignersChoices={cosignersChoices}
                file={templateFiles[id]}
                setFile={setFile}
                hasErrors={hasErrors}
                preview={preview}
                submit={submit}
                integration={integration}
                items={items}
                validator={validator}
              />
            );
          }
          return (
            <Box key={id} border padding="24" mt="16" inactive={loading}>
              {loading && <Loading fixed />}
              <Stack space={4} key={template.name}>
                <Box alignItems="center">
                  <Select
                    options={templateChoices}
                    onChange={(value) => {
                      template.setValue(value);
                      refetch();
                    }}
                    noOptions={() => t('integrations.signet.templates.options.empty')}
                    key={template.value}
                    defaultValue={template.value || null}
                    label={template.label}
                    error={template.error}
                    dataId={template.name}
                    isClearable={false}
                  />
                </Box>
                {!template.value && (
                  <Stack space={2} orientation="horizontal" justify="center" align="center">
                    <Separator />
                    <Text
                      font="semibold14"
                      style={{ textTransform: 'uppercase' }}
                      color="purple80"
                      text={t('common.or')}
                    />
                    <Separator />
                  </Stack>
                )}
                {!template.value && (
                  <FileUpload
                    file={templateFiles[id] || null}
                    onChange={(file) => setFile(id, file)}
                    extensions=".docx, .pdf"
                  />
                )}
                <Stack space={4}>
                  {items.map((data, i) => (
                    <IntegrationField data={data} refetch={refetch} validator={validator} key={i} />
                  ))}
                  {template.value && needsCosigners && (
                    <InlineMultiSelect
                      key={cosigners.name}
                      label={`${cosigners.required && !templateFiles[id] ? '* ' : ''}${
                        cosigners.label
                      }`}
                      options={cosignersChoices}
                      onChange={cosigners.setValue}
                      placeholder={cosigners.label}
                      defaultValue={cosigners.value}
                      error={cosigners.error}
                      dataId={cosigners.name}
                    />
                  )}
                </Stack>
                <Box flex justifyContent="flex-end" mt="20">
                  {!template.value && !templateFiles[id] && (
                    <Box ml="8">
                      <Button
                        variant="secondary"
                        isDisabled={loading}
                        onClick={() => remove(id)}
                        text={t('application.onboarding.integration.cancel')}
                      />
                    </Box>
                  )}
                  {(template.value || templateFiles[id]) && (
                    <>
                      <Box ml="8">
                        <IconButton
                          tooltip={t('application.onboarding.integration.delete')}
                          icon={<IconTrash />}
                          onClick={() => remove(id)}
                        />
                      </Box>
                      <Box ml="8">
                        <IconButton
                          tooltip={t('application.onboarding.integration.preview')}
                          icon={<IconVisibilityShown />}
                          disabled={loading || hasErrors}
                          onClick={() => preview(id)}
                        />
                      </Box>
                      <Box ml="8">
                        <Button
                          variant="primary-blue"
                          isDisabled={loading}
                          onClick={() => submit(false, true, id)}
                          text={t('application.onboarding.integration.save')}
                        />
                      </Box>
                      <Box ml="8">
                        <Button
                          variant="primary"
                          isDisabled={loading || integration.isBlocked || hasErrors}
                          onClick={() => submit(false, false, id)}
                          text={t('integrations.signet.button.submit')}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Stack>
            </Box>
          );
        }
      )}
      {integration.state === 'loaded' && (
        <>
          <Separator mt="24" />
          <Box flex justifyContent="flex-end" mt="24">
            <Button
              variant="secondary"
              iconLeft={<IconAddPlus />}
              onClick={() => integration.appendMultiple()}
              text={t('integrations.signet.button.document.add')}
            />
          </Box>
        </>
      )}
    </>
  );
});

const readURL = (file) => {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = (e) => res(e.target.result);
    reader.onerror = (e) => rej(e);
    reader.readAsDataURL(file);
  });
};
